import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import type { RezaSablonaDto, RezaPortalInfo } from '@gov-nx/api';
import { rezaTemplate, usePoQuery, useAxiosInstance } from '@gov-nx/api';
import type { PageCode } from '@gov-nx/module/page';
import { toDateReadable } from '@gov-nx/utils/common';
import { usePortalInfo } from '../../usePortalInfo';
import { useRezaLocale } from '../../useRezaLocale';

interface RezaTemplateDetailContexts {
	code: PageCode;
	isLoading: boolean;
	isError: boolean;
	template?: RezaSablonaDto;
	keyValues: Record<string, { value: string; isCopyable?: boolean }>;
	portalInfo?: RezaPortalInfo;
}

const Context = createContext<RezaTemplateDetailContexts | null>(null);

type Props = PropsWithChildren<{ code: PageCode; id: string }>;

export const RezaTemplateDetailContextProvider = ({
	children,
	id,
	code,
}: Props) => {
	const { t } = useRezaLocale();
	const { axiosInstance } = useAxiosInstance();
	const query = usePoQuery({
		queryKey: ['reza', 'templates', id],
		queryFn: async () => rezaTemplate(axiosInstance, id),
	});

	const portalInfoQuery = usePortalInfo(id);

	const keyValues: RezaTemplateDetailContexts['keyValues'] = {
		[t('sablony.detail.table.verze')]: { value: query.data?.verze ?? '-' },
		[t('sablony.detail.table.platnost-od')]: {
			value: query.data?.platnostOd
				? toDateReadable(query.data?.platnostOd)
				: '-',
		},
		[t('sablony.detail.table.platnost-do')]: {
			value: query.data?.platnostDo
				? toDateReadable(query.data?.platnostDo)
				: t('sablony.detail.table.platnost-do-neomezeno'),
		},
		[t('sablony.detail.table.popis')]: { value: query.data?.popis ?? '-' },
		[t('sablony.detail.table.identifikator')]: {
			value: query.data?.kod ?? '-',
			isCopyable: !!query.data?.kod,
		},
	};

	return (
		<Context.Provider
			value={{
				code,
				isError: query.isError,
				isLoading: query.isLoading || portalInfoQuery.isLoading,
				template: query.data,
				keyValues,
				portalInfo: portalInfoQuery.data,
			}}>
			{children}
		</Context.Provider>
	);
};

export const useRezaTemplateDetailContext = () => {
	const context = useContext(Context);
	if (!context) {
		throw new Error(
			'useRezaTemplateDetailContext must be used within a RezaTemplateDetailContextProvider'
		);
	}
	return context;
};
